/*
  Quick add: the + on a product card (home-card).

  Two paths from a card:
  - one variant, so there is nothing to choose: add it straight away
  - more than one: fetch quick-add-panel for that product and open it

  The panel is a section fetched by URL rather than a copy of the product
  page, so its markup is ours and it carries only the images and the choice.

  In the panel the size starts unchosen, unless she picked a size on an
  earlier product and it is in stock here. Until a size is chosen the button says
  Choose a size; then Add to cart with the price. The price is what the
  item costs with the cart's Mix & Match tier once it is in, read from
  mix-match.js. After adding, the panel says Added to your cart with Keep
  shopping and View cart, and the drawer is brought up to date behind it.
*/

if (!window.quickAddLoaded) {
  window.quickAddLoaded = true;

  (() => {
    const PANEL_SECTION = 'quick-add-panel';

    // Must match the closing animation in quick-add.css.
    const CLOSE_MS = 300;

    // How long a pointer has to rest on a card before it counts as interest.
    const WARM_DELAY_MS = 120;

    // The last size she picked, the same key as the product page.
    const SIZE_KEY = 'rm-selected-size';

    let overlay = null;
    let panelHost = null;
    let lastTrigger = null;
    let closeTimer = null;
    let warmTimer = null;
    let variants = [];
    let mediaItems = [];
    let dots = [];

    const cache = new Map();

    /* Mix & Match --------------------------------------------------------- */

    const mm = () => window.MixMatch || null;
    const mmCount = () => (mm() ? mm().count : 0);
    const mmPct = (n) => (mm() ? mm().pct(n) : 0);
    const mmUnit = (cents, n) => (mm() ? mm().unit(cents, n) : cents);
    const mmMoney = (cents, fallback) => (mm() ? mm().money(cents) : fallback);

    /* Cart -------------------------------------------------------------- */

    const cartTarget = () => document.querySelector('cart-drawer');

    /*
      From a card with one variant: straight in, and the drawer opens, as
      before. From the panel: the drawer is redrawn without opening, and the
      panel turns into Added to your cart.
    */
    const addToCart = async (variantId, button, fromPanel) => {
      const drawer = cartTarget();

      if (button) button.setAttribute('aria-busy', 'true');

      const body = new FormData();
      body.append('id', variantId);
      body.append('quantity', 1);

      if (drawer && drawer.getSectionsToRender) {
        body.append('sections', drawer.getSectionsToRender().map((section) => section.id));
        body.append('sections_url', window.location.pathname);
      }

      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { Accept: 'application/javascript' },
          body,
        });
        const result = await response.json();

        if (result.status) throw new Error(result.description || result.message);

        if (fromPanel && overlay && !overlay.hidden) {
          if (drawer && drawer.replaceSections && result.sections) drawer.replaceSections(result);
          await showAdded();
          return;
        }

        closePanel();
        if (drawer && drawer.renderContents) drawer.renderContents(result);
        else window.location.href = window.routes.cart_url;
      } catch (error) {
        window.location.href = window.routes.cart_url;
      } finally {
        if (button) button.removeAttribute('aria-busy');
      }
    };

    const showAdded = async () => {
      const choose = overlay.querySelector('[data-qa-choose]');
      const added = overlay.querySelector('[data-qa-added]');
      if (!choose || !added) return;

      // The count for View cart, and the green box, from the cart as it is now.
      if (mm()) await mm().refresh();
      const viewCart = added.querySelector('[data-qa-view-cart]');
      if (viewCart) {
        const n = mmCount();
        viewCart.textContent = n > 0 ? `${viewCart.dataset.label} (${n})` : viewCart.dataset.label;
      }
      if (mm()) mm().render(added);

      choose.hidden = true;
      added.hidden = false;
      overlay.classList.add('is-added');

      const keep = added.querySelector('[data-qa-close]');
      if (keep) keep.focus({ preventScroll: true });
    };

    /* Overlay ----------------------------------------------------------- */

    const onKeydown = (event) => {
      if (event.key === 'Escape') closePanel();
    };

    const build = () => {
      if (overlay) return;

      overlay = document.createElement('div');
      overlay.className = 'qa-overlay';
      overlay.hidden = true;
      overlay.innerHTML =
        '<div class="qa-overlay__panel" role="dialog" aria-modal="true">' +
        '<button type="button" class="qa-overlay__close" data-qa-close>' +
        '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
        '<path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.6"/>' +
        '</svg></button>' +
        '<div class="qa-overlay__body" data-qa-body></div>' +
        '</div>';

      document.body.appendChild(overlay);
      panelHost = overlay.querySelector('[data-qa-body]');

      const closeButton = overlay.querySelector('.qa-overlay__close');
      closeButton.setAttribute('aria-label', 'Close');

      overlay.addEventListener('click', (event) => {
        if (!event.target.closest('.qa-overlay__panel')) {
          closePanel();
          return;
        }

        if (event.target.closest('[data-qa-close]')) {
          closePanel();
          return;
        }

        const viewCart = event.target.closest('[data-qa-view-cart]');
        if (viewCart) {
          const drawer = cartTarget();
          if (drawer && drawer.open) {
            event.preventDefault();
            closePanel();
            drawer.open();
          }
          return;
        }

        const dot = event.target.closest('[data-qa-dot]');
        if (dot) {
          showMedia(Number(dot.dataset.qaDot));
          return;
        }

        const gallery = event.target.closest('[data-qa-media]');
        if (gallery) pageGallery(gallery, event.clientX);
      });

      overlay.addEventListener('change', (event) => {
        if (event.target.matches('[data-qa-option-position]')) {
          if (event.target.matches('[data-qa-size]')) {
            try {
              localStorage.setItem(SIZE_KEY, event.target.value);
            } catch (error) {
              // Private browsing: nothing to remember it in.
            }
          }
          onOptionChange();
        }
      });

      overlay.addEventListener('submit', (event) => {
        const form = event.target.closest('[data-qa-form]');
        if (!form) return;

        event.preventDefault();

        const input = form.querySelector('[data-qa-variant-id]');
        const button = form.querySelector('[data-qa-submit]');

        // No size yet: the button is a nudge, so it points at the sizes.
        if (!input || !input.value) {
          const sizes = form.querySelector('.qa__option--size');
          if (sizes) {
            sizes.classList.remove('is-nudged');
            void sizes.offsetWidth;
            sizes.classList.add('is-nudged');
          }
          return;
        }

        addToCart(input.value, button, true);
      });

      // mix-match.js redraws every Buy more, save more block after a change;
      // the price and the button follow it here.
      document.addEventListener('mixmatch:update', () => {
        if (overlay && !overlay.hidden) onOptionChange();
      });
    };

    const openPanel = () => {
      clearTimeout(closeTimer);
      overlay.classList.remove('is-closing', 'is-added');

      overlay.hidden = false;
      document.body.classList.add('qa-open');
      document.addEventListener('keydown', onKeydown);
    };

    function closePanel() {
      if (!overlay || overlay.hidden || overlay.classList.contains('is-closing')) return;

      document.removeEventListener('keydown', onKeydown);

      const finish = () => {
        overlay.hidden = true;
        overlay.classList.remove('is-closing', 'is-added');
        document.body.classList.remove('qa-open');
        if (lastTrigger) lastTrigger.focus({ preventScroll: true });
      };

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        finish();
        return;
      }

      overlay.classList.add('is-closing');
      closeTimer = setTimeout(finish, CLOSE_MS);
    }

    /* Gallery ----------------------------------------------------------- */

    const buildDots = () => {
      const list = overlay.querySelector('[data-qa-media]');
      const holder = overlay.querySelector('[data-qa-dots]');

      mediaItems = list ? Array.from(list.children) : [];
      dots = [];

      if (!holder) return;
      holder.innerHTML = '';

      if (mediaItems.length < 2) return;

      mediaItems.forEach((item, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'qa__dot';
        dot.dataset.qaDot = index;
        dot.setAttribute('aria-label', index + 1);
        if (index === 0) dot.setAttribute('aria-current', 'true');
        holder.appendChild(dot);
        dots.push(dot);
      });

      let ticking = false;
      list.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            syncDots(list);
            ticking = false;
          });
        },
        { passive: true }
      );
    };

    const syncDots = (list) => {
      const centre = list.scrollLeft + list.clientWidth / 2;

      let active = 0;
      let closest = Infinity;

      mediaItems.forEach((item, index) => {
        const distance = Math.abs(item.offsetLeft + item.offsetWidth / 2 - centre);
        if (distance < closest) {
          closest = distance;
          active = index;
        }
      });

      dots.forEach((dot, index) => dot.toggleAttribute('aria-current', index === active));
    };

    const showMedia = (index, jump) => {
      const list = overlay.querySelector('[data-qa-media]');
      const item = mediaItems[index];
      if (!list || !item) return;

      list.scrollTo({
        left: item.offsetLeft - list.offsetLeft,
        behavior: jump ? 'instant' : 'smooth',
      });
    };

    const showMediaById = (mediaId) => {
      const index = mediaItems.findIndex((item) => Number(item.dataset.mediaId) === mediaId);
      if (index > -1) showMedia(index, true);
    };

    const pageGallery = (list, clientX) => {
      if (mediaItems.length < 2) return;

      const rect = list.getBoundingClientRect();
      const forward = clientX >= rect.left + rect.width / 2;

      list.scrollBy({ left: forward ? rect.width : -rect.width, behavior: 'smooth' });
    };

    const setupCursor = (panel) => {
      const list = panel.querySelector('[data-qa-media]');
      const media = panel.querySelector('.qa__media');
      if (!list || !media || mediaItems.length < 2) return;

      const cursor = document.createElement('div');
      cursor.className = 'qa__cursor';
      cursor.innerHTML =
        '<svg viewBox="0 0 16 12" fill="none" aria-hidden="true">' +
        '<path d="M1 6h14M10.5 1.5 15 6l-4.5 4.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>';
      media.appendChild(cursor);

      list.addEventListener('pointermove', (event) => {
        if (event.pointerType === 'touch') return;

        const rect = media.getBoundingClientRect();
        cursor.classList.add('is-visible');
        cursor.style.transform =
          'translate(' + (event.clientX - rect.left - 22) + 'px,' + (event.clientY - rect.top - 22) + 'px)';

        const forward = event.clientX >= rect.left + rect.width / 2;
        cursor.style.setProperty('--qa-rotate', forward ? '0deg' : '180deg');
      });

      list.addEventListener('pointerleave', () => cursor.classList.remove('is-visible'));
    };

    /* Loading ----------------------------------------------------------- */

    const readVariants = (panel) => {
      const script = panel.querySelector('[data-qa-variants]');
      if (!script) return [];

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return [];
      }
    };

    const fetchPanel = (url) => {
      if (cache.has(url)) return cache.get(url);

      const target = new URL(url, window.location.origin);
      const separator = target.search ? '&' : '?';

      const request = fetch(target.pathname + target.search + separator + 'section_id=' + PANEL_SECTION).then(
        (response) => {
          if (!response.ok) throw new Error('Request failed');
          return response.text();
        }
      );

      cache.set(url, request);
      return request;
    };

    /*
      The last size she picked, chosen for her when this product has it in
      stock in the colour shown.
    */
    const preselectSize = (panel) => {
      let remembered = '';
      try {
        remembered = localStorage.getItem(SIZE_KEY) || '';
      } catch (error) {
        remembered = '';
      }
      if (!remembered) return;

      const inputs = Array.from(panel.querySelectorAll('[data-qa-size]'));
      if (!inputs.length || inputs.some((input) => input.checked)) return;

      const match = inputs.find((input) => input.value.toUpperCase() === remembered.toUpperCase());
      if (!match) return;

      const label = panel.querySelector('label[for="' + match.id + '"]');
      if (label && label.classList.contains('qa__value--unavailable')) return;

      match.checked = true;
    };

    const loadPanel = async (url, trigger) => {
      build();

      lastTrigger = trigger;
      panelHost.innerHTML = '<div class="qa__loading"></div>';
      openPanel();

      try {
        const markup = await fetchPanel(url);
        const parsed = new DOMParser().parseFromString(markup, 'text/html');
        const panel = parsed.querySelector('[data-qa-panel]');
        if (!panel) throw new Error('No panel');

        panelHost.innerHTML = '';
        panelHost.appendChild(panel);

        variants = readVariants(panel);
        buildDots();
        setupCursor(panel);

        if (window.attachDragScroll) {
          panel.querySelectorAll('[data-drag-scroll]').forEach(window.attachDragScroll);
        }

        if (mm()) mm().render(panel);
        onOptionChange();
        preselectSize(panel);
        onOptionChange();
      } catch (error) {
        cache.delete(url);
        window.location.href = url;
      }
    };

    /* Options ----------------------------------------------------------- */

    /*
      An option not chosen yet, the size when the panel opens, matches any
      value: the colour still moves the photo, and the sizes still show
      which are gone in that colour.
    */
    const matches = (item, chosen, skip) =>
      item.options.every((value, index) => index === skip || chosen[index] === undefined || value === chosen[index]);

    const onOptionChange = () => {
      if (!overlay) return;
      const panel = overlay.querySelector('[data-qa-panel]');
      if (!panel) return;

      const optionCount = variants.length ? variants[0].options.length : 0;
      const chosen = new Array(optionCount).fill(undefined);
      panel.querySelectorAll('[data-qa-option-position]:checked').forEach((input) => {
        chosen[Number(input.dataset.qaOptionPosition) - 1] = input.value;
      });

      const complete = chosen.every((value) => value !== undefined);
      const variant = complete ? variants.find((item) => item.options.every((value, index) => value === chosen[index])) : null;

      // The first variant in what has been chosen so far, for the photo and
      // the price before a size is picked.
      const partial = variant || variants.find((item) => matches(item, chosen, -1) && item.available) || variants.find((item) => matches(item, chosen, -1));

      panel.querySelectorAll('[data-qa-option-value]').forEach((label) => {
        const value = chosen[Number(label.dataset.qaOptionValue) - 1];
        label.textContent = value || label.dataset.qaEmpty || '';
      });

      panel.querySelectorAll('[data-qa-option-position]').forEach((input) => {
        const position = Number(input.dataset.qaOptionPosition) - 1;
        const candidate = chosen.slice();
        candidate[position] = input.value;

        const match = variants.find((item) => matches(item, candidate, position) && item.options[position] === input.value && item.available);

        const label = panel.querySelector('label[for="' + input.id + '"]');
        if (label) label.classList.toggle('qa__value--unavailable', !match);
      });

      const input = panel.querySelector('[data-qa-variant-id]');
      const price = panel.querySelector('[data-qa-price]');
      const compare = panel.querySelector('[data-qa-compare]');
      const off = panel.querySelector('[data-qa-off]');
      const thumb = panel.querySelector('[data-qa-thumb]');
      const submit = panel.querySelector('[data-qa-submit]');
      const submitText = panel.querySelector('[data-qa-submit-text]');

      if (input) input.value = variant && variant.available ? variant.id : '';

      // The price with this item in the cart: the next Mix & Match tier.
      const next = mmCount() + 1;
      const pct = mmPct(next);
      const shown = partial || variant;
      const cents = shown ? mmUnit(shown.price_cents, next) : 0;
      const shownMoney = shown ? mmMoney(cents, shown.price) : '';

      if (shown && price) {
        price.textContent = shownMoney;
        price.classList.toggle('qa__price--sale', shown.on_sale || pct > 0);
      }

      if (shown && compare) {
        const showCompare = shown.on_sale || pct > 0;
        compare.textContent = shown.on_sale ? shown.compare_at_price : pct > 0 ? shown.price : '';
        compare.hidden = !showCompare;
      }

      if (off) {
        off.textContent = pct > 0 ? `incl. an extra ${pct}% off your entire cart` : '';
        off.hidden = !(pct > 0);
      }

      if (submit && submitText) {
        submit.classList.remove('is-choose');
        if (!complete) {
          submit.disabled = false;
          submit.classList.add('is-choose');
          submitText.textContent = submit.dataset.labelChoose;
        } else if (!variant) {
          submit.disabled = true;
          submitText.textContent = submit.dataset.labelUnavailable || submit.dataset.labelSold;
        } else if (!variant.available) {
          submit.disabled = true;
          submitText.textContent = submit.dataset.labelSold;
        } else {
          submit.disabled = false;
          submitText.textContent = `${submit.dataset.labelAdd} · ${shownMoney}`;
        }
      }

      if (shown && shown.image && thumb) thumb.src = shown.image;
      if (shown && shown.media_id) showMediaById(shown.media_id);
    };

    /* Triggers ---------------------------------------------------------- */

    const warm = (event) => {
      const trigger = event.target.closest && event.target.closest('[data-quick-add]');
      if (!trigger || trigger.dataset.variantId || !trigger.dataset.productUrl) return;

      if (cache.has(trigger.dataset.productUrl)) return;

      clearTimeout(warmTimer);
      warmTimer = setTimeout(() => {
        fetchPanel(trigger.dataset.productUrl).catch(() => {});
      }, WARM_DELAY_MS);
    };

    const cancelWarm = (event) => {
      if (event.target.closest && event.target.closest('[data-quick-add]')) clearTimeout(warmTimer);
    };

    document.addEventListener('pointerenter', warm, true);
    document.addEventListener('pointerleave', cancelWarm, true);
    document.addEventListener('focusin', warm);

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-quick-add]');
      if (!trigger) return;

      event.preventDefault();
      clearTimeout(warmTimer);

      if (trigger.dataset.variantId) {
        lastTrigger = trigger;
        addToCart(trigger.dataset.variantId, trigger, false);
        return;
      }

      loadPanel(trigger.dataset.productUrl, trigger);
    });
  })();
}